import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLang } from '../../context/LanguageContext';
import { TID } from '../../constants/testIds';
import { Menu, X } from 'lucide-react';
import { useState, useEffect } from 'react';

/**
 * Site header.
 *
 * Design language: Rolex / Sotheby's / Monocle — Deep Navy from the first paint,
 * never a translucent white/cream state. On scroll the navbar loses its 8%
 * transparency and gains a subtle luxury shadow; colours never change.
 */
export default function Header() {
    const { user, logout } = useAuth();
    const { t } = useLang();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    const NAV = [
        { to: '/neighbourhoods', label: t('nav.neighbourhoods'), tid: TID.nav.neighbourhoods },
        { to: '/buyer-centre', label: 'Buyer Centre', tid: 'nav-buyer-centre' },
        { to: '/seller-centre', label: 'Seller Centre', tid: 'nav-seller-centre' },
        { to: '/investor-centre', label: 'Investor Centre', tid: 'nav-investor-centre' },
        { to: '/videos', label: 'Videos', tid: 'nav-videos' },
        { to: '/affordability', label: t('nav.affordability'), tid: TID.nav.affordability },
        { to: '/invest', label: t('nav.invest'), tid: TID.nav.invest },
        { to: '/compare', label: t('nav.compare'), tid: TID.nav.compare },
        { to: '/insights', label: t('nav.insights'), tid: TID.nav.insights },
    ];

    const matchLink = { to: '/match', label: 'The Match', tid: 'nav-match' };

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <header className={`sticky top-0 z-40 bn-header ${scrolled ? 'bn-header-scrolled' : ''}`}>
            <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 lg:px-10 h-[64px]">
                {/* Logo — increased ~20% */}
                <Link to="/" className="flex items-center gap-3" data-testid={TID.nav.logo}>
                    <span
                        className="inline-block w-2.5 h-2.5 rounded-full"
                        style={{ background: '#C8A24A' }}
                    />
                    <span className="font-editorial text-[25px] tracking-tight bn-nav-brand leading-none">
                        BestNeighbourhoods<span className="bn-nav-brand-muted">.ca</span>
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-x-6 xl:gap-x-7 text-[13px] whitespace-nowrap">
                    <NavLink
                        to={matchLink.to}
                        data-testid={matchLink.tid}
                        className={({ isActive }) =>
                            `bn-nav-link flex items-center gap-2 font-medium whitespace-nowrap ${isActive ? 'bn-nav-active' : ''}`
                        }
                    >
                        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: '#C8A24A' }} />
                        {matchLink.label}
                    </NavLink>
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={n.tid}
                            className={({ isActive }) =>
                                `bn-nav-link whitespace-nowrap ${isActive ? 'bn-nav-active' : ''}`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    {user ? (
                        <>
                            <Link
                                to="/dashboard"
                                data-testid={TID.nav.dashboard}
                                className="text-[13px] px-3.5 py-1.5 rounded-md border border-white/40 text-white hover:border-brand-gold hover:text-brand-gold transition-colors"
                            >
                                {user.name?.split(' ')[0] || t('nav.account')}
                            </Link>
                            <button
                                data-testid={TID.nav.logout}
                                onClick={async () => { await logout(); navigate('/'); }}
                                className="text-[12.5px] text-white/60 hover:text-white transition-colors"
                            >
                                {t('nav.signout')}
                            </button>
                        </>
                    ) : (
                        <Link
                            to="/login"
                            data-testid={TID.nav.login}
                            className="text-[13px] font-medium px-4 py-1.5 rounded-md bg-brand-gold text-brand-navy hover:bg-white transition-colors whitespace-nowrap"
                        >
                            {t('nav.signin')}
                        </Link>
                    )}
                </div>

                <button
                    className="lg:hidden p-2 -mr-2 rounded-lg text-white/90 hover:text-brand-gold transition-colors"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Menu"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-white/10 bg-[#0B1F3A]">
                    <div className="px-6 py-5 flex flex-col gap-3.5 text-[15px]">
                        <Link
                            to={matchLink.to}
                            onClick={() => setOpen(false)}
                            className="py-1 font-medium flex items-center gap-2 text-white hover:text-brand-gold transition-colors"
                        >
                            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: '#C8A24A' }} />
                            {matchLink.label}
                        </Link>
                        {NAV.map((n) => (
                            <Link
                                key={n.to}
                                to={n.to}
                                onClick={() => setOpen(false)}
                                className="py-1 text-white/85 hover:text-brand-gold transition-colors"
                            >
                                {n.label}
                            </Link>
                        ))}
                        {user ? (
                            <>
                                <Link to="/dashboard" onClick={() => setOpen(false)} className="py-1 text-white hover:text-brand-gold">{t('nav.dashboard')}</Link>
                                <button onClick={async () => { await logout(); setOpen(false); navigate('/'); }} className="py-1 text-left text-white/55 hover:text-white">{t('nav.signout')}</button>
                            </>
                        ) : (
                            <Link to="/login" onClick={() => setOpen(false)} className="py-1 font-medium text-brand-gold">{t('nav.signin')}</Link>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
